/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  getGlobalVariables,
  getWorkflowBackoffice,
  getWorkflowElementById as getWorkflowElementByIdApi,
  getWorkflowElements,
  getWorkflowEngine,
  getWorkflowEngineById,
} from "@/api/workflow";
import httpRequest from "@/auth/http-client";
import { buildPaginationQuery } from "@/helpers/request-helpers";
import { IGlobalVariable } from "@/interfaces/global-variable.interfaces";
import IWorkflowElement from "@/interfaces/workflow-element.interfaces";
import IWorkflowEngine from "@/interfaces/workflow-engine.interfaces";
import { IWorkflow, IWorkflowInput, IWorkflowSave } from "@/interfaces/workflow.interface";
import { IFilter } from "@/types/filter.types";
import { PaginatedResponse, RequestPagination } from "@/types/requests.types";

// removed unused IGlobalVariableResponse in favor of direct API return types

class WorkflowService {
  private baseUrl = "api/v1/workflow";

  async getAll(pagination: RequestPagination): Promise<PaginatedResponse<IWorkflow>> {
    const paginationQuery = buildPaginationQuery(pagination);
    const workflows = await httpRequest.get(`${this.baseUrl}?${paginationQuery}`);
    return workflows;
  }

  async getAllBackoffice(pagination: RequestPagination): Promise<PaginatedResponse<IWorkflow>> {
    // Usar API interna ao invés de chamada para Next.js
    return await getWorkflowBackoffice(pagination);
  }

  async geWorkflowEngineBackofficeById(workflowId: number): Promise<IWorkflowEngine> {
    return await getWorkflowEngineById(workflowId);
  }

  async getAllWorkflowEngineBackoffice(
    pagination: RequestPagination
  ): Promise<PaginatedResponse<IWorkflowEngine>> {
    return await getWorkflowEngine(pagination);
  }

  async getAllWorkflowEngineBackofficeAll(
    batchSize = 100
  ): Promise<PaginatedResponse<IWorkflowEngine>> {
    const firstPage = await this.getAllWorkflowEngineBackoffice({
      pageNumber: 1,
      pageSize: batchSize,
    });

    const itemsMap = new Map(firstPage.items.map((item) => [item.id, item]));
    let nextPageNumber = firstPage.pageNumber + 1;
    let hasNextPage = firstPage.hasNextPage;

    while (hasNextPage && itemsMap.size < firstPage.totalCount) {
      const page = await this.getAllWorkflowEngineBackoffice({
        pageNumber: nextPageNumber,
        pageSize: batchSize,
      });

      page.items.forEach((item) => {
        itemsMap.set(item.id, item);
      });

      hasNextPage = page.hasNextPage;
      nextPageNumber = page.pageNumber + 1;
    }

    const items = Array.from(itemsMap.values());

    return {
      items,
      totalCount: Math.max(firstPage.totalCount, items.length),
      pageNumber: 1,
      pageSize: items.length,
      hasNextPage: false,
      hasPreviousPage: false,
    };
  }

  async getWorkflowElementById(elementId: number): Promise<IWorkflowElement> {
    return await getWorkflowElementByIdApi(elementId);
  }

  async getAllWorkflowElements(
    pagination: RequestPagination
  ): Promise<PaginatedResponse<IWorkflowElement>> {
    return await getWorkflowElements(pagination);
  }

  async filter(
    filter: IFilter<IWorkflow>[],
    pagination: RequestPagination
  ): Promise<PaginatedResponse<IWorkflow>> {
    const paginationQuery = buildPaginationQuery(pagination);

    const response = await httpRequest.post(`${this.baseUrl}/filter?${paginationQuery}`, {
      filter,
    });

    return response;
  }

  async create(formData: IWorkflowInput): Promise<void> {
    await httpRequest.post(this.baseUrl, formData);
  }

  async getById(workflowId: number): Promise<IWorkflow> {
    const workflow = await httpRequest.get(`${this.baseUrl}/${workflowId}`);

    return workflow;
  }

  async update(formData: IWorkflowInput, workflowId: number): Promise<void> {
    await httpRequest.put(`${this.baseUrl}/${workflowId}`, formData);
  }

  async updateAvatar(file: File, workflowId: number): Promise<IWorkflow> {
    const formData = new FormData();
    formData.append("file", file);

    return await httpRequest.put(`${this.baseUrl}/${workflowId}/avatar`, formData, {
      headers: {
        // Override default to allow Axios to set correct multipart boundary
        "Content-Type": "multipart/form-data",
      },
    });
  }

  async downloadAvatar(workflowId: number): Promise<Blob> {
    return await httpRequest.get(`${this.baseUrl}/${workflowId}/avatar`, {
      responseType: "blob",
    });
  }

  async removeAvatar(workflowId: number): Promise<void> {
    await httpRequest.delete(`${this.baseUrl}/${workflowId}/avatar`);
  }

  async getCustomerSupportsCarousel(customerId: number): Promise<boolean> {
    return await httpRequest.get<boolean>(`${this.baseUrl}/${customerId}/supports-carousel`);
  }

  async delete(workflowId: number): Promise<void> {
    await httpRequest.delete(`${this.baseUrl}/${workflowId}`);
  }

  async save(formData: IWorkflowSave): Promise<void> {
    await httpRequest.post(`${this.baseUrl}/copytemplate`, formData);
  }

  async getAllGlobalVariables(): Promise<PaginatedResponse<IGlobalVariable>> {
    return await getGlobalVariables();
  }
}

const workflowService = new WorkflowService();
export default workflowService;
